'use client'
import { createContext, useEffect, useMemo, useReducer } from 'react'
import { _debounce } from 'utils/shared'
import { getWindowDetails } from 'utils/viewport'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { mainProductConfig } from '@/constants/mainProductConfig'

const LayoutContext = createContext()

// default to iphone size - xs
const DEFAULT_SIZE_SMALL = Object.freeze({
  breakpointPixelSize: 320,
  breakpointLetterSize: 'xs',
  screenWidth: 390,
  screenHeight: 844,
  calculatedClient: false,
})

// 1280x800px `xl` default desktop size
const DEFAULT_SIZE = Object.freeze({
  breakpointPixelSize: 1280,
  breakpointLetterSize: 'xl',
  screenWidth: 1281,
  screenHeight: 800,
  calculatedClient: false,
})

const layoutReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MENU_CONDENSED':
      return {
        ...state,
        menuCondensed: action.value,
      }
    case 'SET_VIEWPORT_SIZE':
      return {
        ...state,
        viewportSize: action.value,
      }
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}
const mainURL = mainProductConfig[process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve']?.URL

const LayoutProvider = ({
  children,
  isMobileUA = false,
  siteEnv = 'achieve',
  siteInfo = { url: mainURL },
  leadId,
}) => {
  const { query } = useAppRouter()
  const [state, dispatch] = useReducer(layoutReducer, {
    siteInfo,
    siteEnv,
    isMobileUA,
    menuCondensed: false,
    viewportSize: isMobileUA ? DEFAULT_SIZE_SMALL : DEFAULT_SIZE,
    leadId,
  })

  // LayoutProvider init hook. Only needs to run on initial browser render
  useEffect(() => {
    function getViewPortData() {
      const result = getWindowDetails()
      // Window measured results or a default size based on UA detection on server
      return result ?? (isMobileUA ? DEFAULT_SIZE_SMALL : DEFAULT_SIZE)
    }

    const handleResize = _debounce(
      () => {
        dispatch({ type: 'SET_VIEWPORT_SIZE', value: getViewPortData() })
      },
      250,
      { maxWait: 250 }
    )

    // Always initialize the resize event listener
    window.addEventListener('resize', handleResize)

    // Only initialize initial viewport data if format is not defined in the url query params
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !query?.device && dispatch({ type: 'SET_VIEWPORT_SIZE', value: getViewPortData() })

    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue = useMemo(() => ({ dispatch, state }), [dispatch, state])
  return <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>
}

export { LayoutProvider, LayoutContext }
